import React from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

const statsData = [
  {
    id: 1,
    value: 2820,
    percent: 64,
    color: "#52c234",
    label: "Deployed Servers",
  },
  { id: 2, value: 1526, percent: 46, color: "#f83600", label: "Total Clients" },
  {
    id: 3,
    value: 3254,
    percent: 38,
    color: "#3369e7",
    label: "Days of Uptime",
  },
  {
    id: 4,
    value: 4257,
    percent: 55,
    color: "#66128c",
    label: "Websites Hosted",
  },
];

export default function StatsSection() {
  const controls = useAnimation();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });

  React.useEffect(() => {
    if (inView) controls.start("visible");
  }, [inView, controls]);

  return (
    <section className="bg-gray-100 py-20" id="stats">
      <div className="container mx-auto px-4 text-center">
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-gray-800">Stats</h2>
        </div>

        <div
          ref={ref}
          className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-6 justify-items-center"
        >
          {statsData.map((stat) => (
            <motion.div
              key={stat.id}
              variants={{
                hidden: { opacity: 0, y: 50 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
              }}
              initial="hidden"
              animate={controls}
              className="flex flex-col items-center"
            >
              <AnimatedCircle stat={stat} animate={inView} />
              <span className="mt-4 text-gray-700 font-medium">
                {stat.label}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AnimatedCircle({ stat, animate }) {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    if (!animate) return;

    let rafId;
    const durationMs = 1500; // total animation time
    const startValue = 0;
    const endValue = stat.percent;
    const startTime = performance.now();

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const tick = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / durationMs);
      const eased = easeOutCubic(t);
      const value = startValue + (endValue - startValue) * eased;
      setProgress(value);
      if (t < 1) rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [animate, stat.percent]);

  return (
    <div
      className="relative flex items-center justify-center rounded-full border-4 border-gray-200 bg-white"
      style={{ width: 180, height: 180 }}
    >
      {/* Progress circle */}
      <div className="absolute inset-0">
        <CircularProgressbar
          value={progress}
          strokeWidth={12}
          styles={buildStyles({
            pathColor: stat.color,
            trailColor: "transparent",
            strokeLinecap: "butt",
            // Disable internal transition; we drive smoothness via rAF
            pathTransitionDuration: 0,
          })}
        />
      </div>

      {/* Clean slightly bold number — no shadow */}
      <span className="absolute text-2xl font-semibold text-gray-900 select-none">
        {stat.value}
      </span>
    </div>
  );
}
